/**
 * Composable для управления комбо-товаром в корзине.
 * Инкапсулирует: проверку наличия в корзине, количество, изменение количества через API.
 *
 * Был выделен из MoreAboutDishRefactored.vue (SRP):
 * ранее эта логика дублировалась в comboInCart, comboQuantity, handleComboQuantityChange.
 */

import { ref, computed, type Ref, type ComputedRef } from 'vue';
import { consola } from 'consola';
import type { ComboCartSelection } from './useComboBuilder';
import type { ComboSelections , ComboProduct } from '@entities/product';
import { useCartStore , apiCartInstance } from '@entities/order';
import { useAuthStore } from '~/features/auth/@x/combo';


import { findComboCartItem, normalizeSelections } from '@/utils/comboSelections';

const logger = consola.withTag('useComboCart');

export interface UseComboCartOptions {
  combo: ComputedRef<ComboProduct | null>;
  branchId: ComputedRef<string | null>;
  /**
   * Выбор внутри комбо: либо карта { groupId: productId } (легаси),
   * либо готовый payload конструктора с модификаторами слотов.
   */
  selections: Ref<ComboSelections> | ComputedRef<ComboCartSelection[]>;
}

export function useComboCart({ combo, branchId, selections }: UseComboCartOptions) {
  const cartStore = useCartStore();
  const storeAuth = useAuthStore();

  const isLoading = ref(false);

  /** Есть ли данное комбо (с текущим набором selections) в корзине */
  const comboInCart = computed<boolean>(() => {
    if (!combo.value || !branchId.value) return false;
    return !!findComboCartItem(cartStore.cart, combo.value.id, branchId.value, selections.value);
  });

  /** Количество данного комбо в корзине */
  const comboQuantity = computed<number>(() => {
    if (!combo.value || !branchId.value) return 0;
    const item = findComboCartItem(cartStore.cart, combo.value.id, branchId.value, selections.value);
    return item?.quantity ?? 0;
  });

  /**
   * Изменяет количество комбо в корзине.
   * newQuantity <= 0 → удаление; > current → increase; < current → decrease.
   */
  const handleComboQuantityChange = async (newQuantity: number): Promise<void> => {
    if (!combo.value || !branchId.value) return;

    const normalizedSelections = normalizeSelections(selections.value);
    if (!normalizedSelections) return;

    const currentItem = findComboCartItem(
      cartStore.cart,
      combo.value.id,
      branchId.value,
      selections.value,
    );
    const currentQuantity = currentItem?.quantity ?? 0;
    const delta = newQuantity - currentQuantity;

    if (delta === 0) return;

    const numericBranchId = Number(branchId.value);
    isLoading.value = true;

    try {
      if (!storeAuth.isLoggedIn) {
        await _handleGuestQuantityChange(currentItem?.id, newQuantity, numericBranchId);
        return;
      }
      await _handleAuthorizedQuantityChange(
        combo.value.id,
        numericBranchId,
        normalizedSelections,
        currentQuantity,
        newQuantity,
        delta,
      );
    } catch (error) {
      logger.error('Error changing combo quantity', error);
      await cartStore.loadCartFromServer(numericBranchId);
    } finally {
      isLoading.value = false;
    }
  };

  // ─── Private helpers ───────────────────────────────────────────────────────

  const _handleGuestQuantityChange = async (
    itemId: string | undefined,
    newQuantity: number,
    numericBranchId: number,
  ) => {
    if (!itemId) return;
    await (newQuantity <= 0 ? cartStore.removeFromCart(itemId, numericBranchId, [], []) : cartStore.updateQuantity(itemId, newQuantity, numericBranchId, [], []));
  };

  const _handleAuthorizedQuantityChange = async (
    comboId: number,
    numericBranchId: number,
    normalizedSelections: ReturnType<typeof normalizeSelections>,
    currentQuantity: number,
    newQuantity: number,
    delta: number,
  ) => {
    const basePayload = {
      item_type: 'combo' as const,
      combo_id: comboId,
      branch_id: String(numericBranchId),
      combo_selections: normalizedSelections!,
    };

    let response;
    if (newQuantity <= 0) {
      response = await apiCartInstance.decreaseQuantity({ ...basePayload, amount: currentQuantity });
    } else if (delta > 0) {
      response = await apiCartInstance.increaseQuantity({ ...basePayload, amount: delta });
    } else {
      response = await apiCartInstance.decreaseQuantity({ ...basePayload, amount: Math.abs(delta) });
    }

    if (response?.cart) {
      await cartStore.updateCartFromServerResponse(response.cart);
    }
  };

  return {
    isLoading,
    comboInCart,
    comboQuantity,
    handleComboQuantityChange,
  };
}
